/**
 * Advanced Location Search Utility
 * Provides sophisticated filtering with search operators and label support
 */

import LabelStorage from './labelStorage';

/**
 * Parse search query and extract filters
 * @param {string} searchQuery - The raw search query
 * @returns {Object} Parsed search criteria
 */
export const parseSearchQuery = (searchQuery) => {
  if (!searchQuery || typeof searchQuery !== 'string') {
    return { text: '', filters: [] };
  }

  const query = searchQuery.trim();
  const filters = [];
  let remainingText = query;

  // Extract operator-based filters
  const operatorPattern = /(type|label|devices|status):([^\s]+)/gi;
  let match;

  while ((match = operatorPattern.exec(query)) !== null) {
    const [fullMatch, operator, value] = match;

    filters.push({
      type: operator.toLowerCase(),
      value: value,
      raw: fullMatch,
    });

    // Remove this filter from remaining text
    remainingText = remainingText.replace(fullMatch, '').trim();
  }

  return {
    text: remainingText,
    filters: filters,
  };
};

/**
 * Apply search filters to a location node
 * @param {Object} location - The location object
 * @param {Object} searchCriteria - Parsed search criteria
 * @param {number} deviceCount - Number of devices at this location
 * @returns {boolean} Whether the location matches the search
 */
export const matchesSearchCriteria = (location, searchCriteria, deviceCount = 0) => {
  const { text, filters } = searchCriteria;

  // Text search on location name
  if (text) {
    const locationName = location.friendlyName || location.name || '';
    const description = location.description || '';
    const searchText = text.toLowerCase();

    const nameMatch = locationName.toLowerCase().includes(searchText);
    const descMatch = description.toLowerCase().includes(searchText);

    if (!nameMatch && !descMatch) {
      return false;
    }
  }

  // Apply operator filters
  for (const filter of filters) {
    if (!matchesFilter(location, filter, deviceCount)) {
      return false;
    }
  }

  return true;
};

/**
 * Check if location matches a specific filter
 * @param {Object} location - The location object
 * @param {Object} filter - Filter object {type, value}
 * @param {number} deviceCount - Number of devices
 * @returns {boolean} Whether location matches the filter
 */
const matchesFilter = (location, filter, deviceCount) => {
  const { type, value } = filter;

  switch (type) {
    case 'type': {
      // Location type filter - handle both numeric IDs and string names
      const locationTypeValue = location.locationTypeModel?.value || location.locationType;

      // Map numeric location types to names
      const typeMap = {
        1: 'site',
        2: 'building',
        3: 'floor',
        4: 'unit',
        5: 'room',
        6: 'hallway',
        7: 'restroom',
        8: 'stock'
      };

      let locationType;
      if (typeof locationTypeValue === 'number') {
        // Convert numeric type to string name
        locationType = typeMap[locationTypeValue] || String(locationTypeValue);
      } else if (typeof locationTypeValue === 'string') {
        locationType = locationTypeValue.toLowerCase();
      } else {
        locationType = String(locationTypeValue || '').toLowerCase();
      }

      return locationType === value.toLowerCase();
    }

    case 'label': {
      // Label filter
      const locationId = location.id || location._id || location.location_UUID;
      const labels = LabelStorage.getLabelsForLocation(locationId);
      return labels.some(label =>
        label.name.toLowerCase() === value.toLowerCase()
      );
    }

    case 'devices': {
      // Device count filter
      return matchesDeviceCountFilter(deviceCount, value);
    }

    case 'status': {
      // Status filter (if implemented)
      const statusValue = location.status;
      const status = typeof statusValue === 'string'
        ? statusValue.toLowerCase()
        : String(statusValue || '').toLowerCase();
      return status === value.toLowerCase();
    }

    default:
      return true;
  }
};

/**
 * Match device count with comparison operator
 * @param {number} count - Actual device count
 * @param {string} expression - Expression like ">5", "<=10", "=0"
 * @returns {boolean} Whether count matches expression
 */
const matchesDeviceCountFilter = (count, expression) => {
  // Parse operator and value
  const match = expression.match(/^(>=|<=|>|<|=)?(\d+)$/);

  if (!match) {
    // No valid operator, try direct number comparison
    const num = parseInt(expression);
    return !isNaN(num) && count === num;
  }

  const [, operator = '=', valueStr] = match;
  const value = parseInt(valueStr);

  switch (operator) {
    case '>':
      return count > value;
    case '<':
      return count < value;
    case '>=':
      return count >= value;
    case '<=':
      return count <= value;
    case '=':
    default:
      return count === value;
  }
};

/**
 * Filter location tree recursively with search criteria
 * @param {Array} locations - Array of location nodes
 * @param {Object} searchCriteria - Parsed search criteria
 * @param {Object} deviceCounts - Map of locationId -> device count
 * @returns {Array} Filtered location tree
 */
export const filterLocationTree = (locations, searchCriteria, deviceCounts = {}) => {
  if (!locations || !Array.isArray(locations)) {
    return [];
  }

  // If no search criteria, return all
  if (!searchCriteria.text && searchCriteria.filters.length === 0) {
    return locations;
  }

  return locations.reduce((filtered, location) => {
    const locationId = location.id || location._id || location.location_UUID;
    const deviceCount = deviceCounts[locationId] || location.deviceCount || 0;

    // Check if current location matches
    const locationMatches = matchesSearchCriteria(location, searchCriteria, deviceCount);

    // Recursively filter children
    const filteredChildren = location.children
      ? filterLocationTree(location.children, searchCriteria, deviceCounts)
      : [];

    // Include location if it matches OR has matching children
    if (locationMatches || filteredChildren.length > 0) {
      filtered.push({
        ...location,
        children: filteredChildren,
      });
    }

    return filtered;
  }, []);
};

/**
 * Get all matching location IDs from filtered tree
 * @param {Array} locations - Filtered location tree
 * @returns {Array} Array of matching location IDs
 */
export const getMatchingLocationIds = (locations) => {
  const ids = [];

  const collectIds = (nodes) => {
    nodes.forEach(node => {
      const id = node.id || node._id || node.location_UUID;
      if (id) ids.push(id);
      if (node.children && node.children.length > 0) {
        collectIds(node.children);
      }
    });
  };

  collectIds(locations);
  return ids;
};

/**
 * Highlight search terms in text
 * @param {string} text - Text to highlight
 * @param {string} searchTerm - Term to highlight
 * @returns {Array} Array of text parts with highlight info
 */
export const highlightSearchTerm = (text, searchTerm) => {
  if (!searchTerm || !text) {
    return [{ text, highlight: false }];
  }

  const parts = [];
  const lowerText = text.toLowerCase();
  const lowerTerm = searchTerm.toLowerCase();
  let lastIndex = 0;

  let index = lowerText.indexOf(lowerTerm);
  while (index !== -1) {
    // Add non-highlighted part
    if (index > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, index),
        highlight: false,
      });
    }

    // Add highlighted part
    parts.push({
      text: text.substring(index, index + searchTerm.length),
      highlight: true,
    });

    lastIndex = index + searchTerm.length;
    index = lowerText.indexOf(lowerTerm, lastIndex);
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      text: text.substring(lastIndex),
      highlight: false,
    });
  }

  return parts;
};

/**
 * Build device count map from locations
 * @param {Array} locations - Location array (can be hierarchical)
 * @returns {Object} Map of locationId -> device count
 */
export const buildDeviceCountMap = (locations) => {
  const countMap = {};

  const traverse = (nodes) => {
    nodes.forEach(node => {
      const id = node.id || node._id || node.location_UUID;
      if (id) {
        countMap[id] = node.deviceCount || 0;
      }
      if (node.children) {
        traverse(node.children);
      }
    });
  };

  traverse(locations);
  return countMap;
};

export default {
  parseSearchQuery,
  matchesSearchCriteria,
  filterLocationTree,
  getMatchingLocationIds,
  highlightSearchTerm,
  buildDeviceCountMap,
};
